import { inject, Injectable } from '@angular/core';
import { APP_CONFIG, AppConfig } from '@cfg/env/app.config';
import { AppError } from '@domain-utils/app-error';
import { AppErrorItem } from '@domain-utils/app-error-item';
import { AppHttpClientApi } from '@helpers/api/http-client-api';
import { ApiException } from '@helpers/errors/api-error';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import {
  ContentReportLoadResponse,
  ReportLoad,
  ReportLoadPage
} from './models/load.model';
import { mockReportLoads } from '@helpers/api/mocks/report-Load.mock';
import { SERVICE_ERROR_CONSTANTS } from './constants/service-error.const';

@Injectable({
  providedIn: 'root'
})
export class ReportLoadsService {
  private readonly http = inject(AppHttpClientApi);
  private readonly appConfig: AppConfig = inject(APP_CONFIG);
  private readonly loadDNIResource = 'cargamasivaemp/bulk-loads/dni/';
  private readonly loadPolicyResource = 'cargamasivaemp/bulk-loads/policy/';
  private readonly loadIdResource = 'cargamasivaemp/bulk-loads/';
  private readonly deleteLoadResource = 'cargamasivaemp/bulk-loads/delete/';

  getLoadsByDNI(
    dni: string,
    page: number,
    size: number
  ): Observable<ReportLoadPage> {
    return this.fetchLoads<ContentReportLoadResponse>(
      `${this.loadDNIResource}${dni}?page=${page}&size=${size}`,
      (response) => this.normalizeReports(response)
    );
  }

  getLoadsByPolicy(
    policyNumber: string,
    page: number,
    size: number
  ): Observable<ReportLoadPage> {
    return this.fetchLoads<ContentReportLoadResponse>(
      `${this.loadPolicyResource}${policyNumber}?page=${page}&size=${size}`,
      (response) => this.normalizeReports(response)
    );
  }

  getLoadById(loadId: string): Observable<ReportLoad> {
    return this.fetchLoad<ReportLoad>(
      `${this.loadIdResource}${loadId}`,
      (response) => this.normalizeReport(response)
    );
  }

  deleteLoad(loadId: string): Observable<void> {
    if (this.appConfig.useMocks) {
      return of(undefined);
    }

    return this.http
      .delete(`${this.deleteLoadResource}${encodeURIComponent(loadId)}`)
      .pipe(catchError((error: unknown) => this.handleLoadError(error)));
  }

  private fetchLoads<TResponse>(
    url: string,
    normalize: (response: TResponse | undefined) => ReportLoadPage
  ): Observable<ReportLoadPage> {
    if (this.appConfig.useMocks) {
      const totalElements = mockReportLoads.length;
      return of(
        this.normalizeReports({
          content: mockReportLoads,
          currentPage: 0,
          size: totalElements,
          totalElements,
          totalPages: totalElements > 0 ? 1 : 0
        })
      );
    }

    return this.http.getPlain<TResponse>(url).pipe(
      map((response) => normalize(response)),
      catchError((error: unknown) => this.handleLoadError(error))
    );
  }

  private fetchLoad<TResponse>(
    url: string,
    normalize: (response: TResponse | undefined) => ReportLoad
  ): Observable<ReportLoad> {
    if (this.appConfig.useMocks) {
      const mock = mockReportLoads[0];
      return of(this.mapReport(mock));
    }

    return this.http.getPlain<TResponse>(url).pipe(
      map((response) => normalize(response)),
      catchError((error: unknown) => this.handleLoadError(error))
    );
  }

  private handleLoadError(error: unknown): Observable<never> {
    if (error instanceof AppError) {
      return throwError(() => error);
    }

    if (error instanceof ApiException) {
      const codes: AppErrorItem[] = error.error.map((item) => ({
        key: item.key,
        message: item.message
      }));

      return throwError(
        () => new AppError(SERVICE_ERROR_CONSTANTS.REPORT.FETCH_FAILED, codes)
      );
    }
    return throwError(
      () =>
        new AppError(SERVICE_ERROR_CONSTANTS.REPORT.FETCH_FAILED, [
          {
            key: SERVICE_ERROR_CONSTANTS.REPORT.UNKNOWN_ERROR.key,
            message:
              error instanceof Error
                ? error.message
                : SERVICE_ERROR_CONSTANTS.REPORT.UNKNOWN_ERROR.message
          }
        ])
    );
  }

  private normalizeReports(
    data: ContentReportLoadResponse | undefined
  ): ReportLoadPage {
    if (!data || !Array.isArray(data.content)) {
      return {
        items: [],
        totalRecords: 0
      };
    }

    const items = data.content.map((item) => this.mapReport(item));
    const totalRecords = data.totalElements ?? items.length;

    return {
      items,
      totalRecords
    };
  }

  private normalizeReport(data: ReportLoad | undefined): ReportLoad {
    if (!data) {
      return {} as ReportLoad;
    }
    return this.mapReport(data);
  }

  private mapReport(load: ReportLoad): ReportLoad {
    const createdAt = new Date(load?.createdAt ?? '');
    return {
      ...load,
      createdAt
    };
  }
}
